import { memo, useEffect, useRef } from 'react';
import lottie from 'lottie-web';

const LottieAnimation = memo((props) => {
	const refs = useRef();
	const anim = useRef();
	const delayTimeout = useRef();

	useEffect(() => {
		if (anim.current) {
			anim.current.destroy();
		}
		anim.current = lottie.loadAnimation({
			container: refs.current,
			loop: props.loop,
			autoplay: props.autoplay,
			animationData: props.animData,
			renderer: props.render || 'canvas',
			rendererSettings: {
				preserveAspectRatio: props.ratio || '',
			},
		});
	}, [props.animData, props.loop, props.autoplay, props.ratio, props.render]);

	useEffect(() => {
		const animation = anim.current;
		if (!animation) return;

		if (props.duration) {
			const totalFrames = animation.totalFrames || (animation.animationData && animation.animationData.op);
			const frameRate = animation.frameRate || (animation.animationData && animation.animationData.fr) || 60;
			const origDuration = totalFrames / frameRate;
			const playbackSpeed = origDuration / props.duration;
			animation.setSpeed(playbackSpeed);
		} else if (props.speed) {
			animation.setSpeed(props.speed);
		} else {
			animation.setSpeed(1);
		}

		const handleComplete = () => {
			if (props.onEnd) props.onEnd();
			if (props.parentMethod) props.parentMethod();
		};

		animation.addEventListener('complete', handleComplete);

		if (refs.current) {
			const canvas = refs.current.querySelector('canvas');
			if (canvas) {
				canvas.style.width = '100%';
				canvas.style.height = '100%';
			}
		}

		function handleLoopComplete() {
			if (delayTimeout.current) {
				clearTimeout(delayTimeout.current);
			}
			if (props.loopDelay && props.loop) {
				animation.pause();
				delayTimeout.current = setTimeout(() => {
					animation.play();
				}, props.loopDelay);
			}
			if (props.onCompletePause) {
				animation.pause();
			}
		}
		animation.addEventListener('loopComplete', handleLoopComplete);

		if (props.isPaused) animation.pause();
		if (props.isPlay) {
			animation.setDirection(1);
			if (typeof props.delay === 'number') {
				setTimeout(() => {
					animation.play();
				}, props.delay);
			} else {
				animation.play();
			}
		}
		if (props.isReverse) {
			animation.setDirection(-1);
			animation.play();
		}

		return () => {
			animation.removeEventListener('complete', handleComplete);
			animation.removeEventListener('loopComplete', handleLoopComplete);
			if (delayTimeout.current) clearTimeout(delayTimeout.current);
		};
	}, [props.isPlay, props.isPaused, props.isReverse, props.goToAndStop, props.onEnd, props.parentMethod, props.onCompletePause, props.duration, props.speed, props.loopDelay, props.loop]);

	return (
		<div
			ref={refs}
			className={`lottie-wrap ${props.className || ''}`}
		/>
	);
});

LottieAnimation.displayName = 'LottieAnimation';

export default LottieAnimation;
